import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, FilterChips, SearchInput, Shimmer } from '@teals/ui-web';
import { useAuth } from '../../hooks';
import { useGetWorkflowsQuery, type WorkflowSummary } from '../../store/api';

const Page = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px;
  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin: 0 0 4px;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 16px;
`;

const WorkflowCard = styled(Card)`
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover,
  &:focus-visible {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const Name = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
`;

const Description = styled.p`
  margin: 0 0 12px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Footer = styled.div`
  border-top: 1px solid var(--border);
  padding-top: 10px;
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

export const LIFECYCLE_BADGES = {
  draft: { variant: 'neutral', label: 'Draft' },
  active: { variant: 'success', label: 'Active' },
  paused: { variant: 'warning', label: 'Paused' },
  archived: { variant: 'neutral', label: 'Archived' },
} as const;

const TRIGGER_LABELS = { api: 'API only', schedule: 'Schedule', event: 'Event' } as const;

const Workflows: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const { data: workflows = [], isLoading, isError } = useGetWorkflowsQuery(orgId, {
    skip: !orgId,
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [trigger, setTrigger] = useState('all');

  const unarchived = useMemo(
    () => workflows.filter((item) => item.lifecycle !== 'archived'),
    [workflows],
  );

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Archived workflows only appear under their own filter.
    return (status === 'all' ? unarchived : workflows).filter(
      (item) =>
        (status === 'all' || item.lifecycle === status) &&
        (trigger === 'all' || item.trigger_kind === trigger) &&
        (!term ||
          item.name.toLowerCase().includes(term) ||
          (item.title || '').toLowerCase().includes(term)),
    );
  }, [workflows, unarchived, search, status, trigger]);

  const statusChips = ['all', 'draft', 'active', 'paused', 'archived'].map((id) => ({
    id,
    label: id === 'all' ? 'All' : LIFECYCLE_BADGES[id as keyof typeof LIFECYCLE_BADGES].label,
    count:
      id === 'all'
        ? unarchived.length
        : workflows.filter((item) => item.lifecycle === id).length,
  }));
  const triggerChips = [
    { id: 'all', label: 'Any start' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'event', label: 'Event' },
    { id: 'api', label: 'API only' },
  ];
  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setTrigger('all');
  };

  const card = (item: WorkflowSummary) => (
    <WorkflowCard
      key={item.id}
      padding={16}
      elevation="sm"
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/workflows/${item.id}`)}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') navigate(`/workflows/${item.id}`);
      }}
    >
      <Name>{item.title || item.name}</Name>
      <Badges>
        <Badge variant={LIFECYCLE_BADGES[item.lifecycle].variant} size="sm">
          {LIFECYCLE_BADGES[item.lifecycle].label}
        </Badge>
        <Badge variant="info" size="sm">
          {TRIGGER_LABELS[item.trigger_kind]}
        </Badge>
      </Badges>
      <Description>{item.description}</Description>
      <Footer>
        {item.agent_count} {item.agent_count === 1 ? 'agent' : 'agents'}
        {item.active_version ? ` · Version ${item.active_version} live` : ' · Not live yet'}
      </Footer>
    </WorkflowCard>
  );

  return (
    <Page>
      <Header>
        <div>
          <Title>Workflows</Title>
          <Subtitle>
            Run a job through several agents, from a schedule, an event, or an API call.
          </Subtitle>
        </div>
        <Button onClick={() => navigate('/workflows/new')}>New workflow</Button>
      </Header>
      {isLoading && <Shimmer height="160px" />}
      {isError && (
        <EmptyState
          title="Workflows could not be loaded"
          description="Reload the page to try again."
        />
      )}
      {!isLoading && !isError && workflows.length === 0 && (
        <EmptyState
          title="No workflows yet"
          description="Describe a job and the Workflow Builder draws the path. Your agents do not have to exist yet."
          action={<Button onClick={() => navigate('/workflows/new')}>New workflow</Button>}
        />
      )}
      {workflows.length > 0 && (
        <>
          <Toolbar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name" />
            <FilterChips items={statusChips} activeId={status} onChange={setStatus} />
            <FilterChips items={triggerChips} activeId={trigger} onChange={setTrigger} />
          </Toolbar>
          {status === 'all' && unarchived.length === 0 ? (
            <EmptyState
              title="All your workflows are archived"
              description="Archived workflows stay under the Archived filter."
              action={
                <Button variant="outline" onClick={() => setStatus('archived')}>
                  View archived
                </Button>
              }
            />
          ) : shown.length === 0 ? (
            <EmptyState
              title="No workflows match"
              description="Nothing matches this search and these filters."
              action={
                <Button variant="outline" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <Grid>{shown.map(card)}</Grid>
          )}
        </>
      )}
    </Page>
  );
};

export default Workflows;
